import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, InputAdornment, List, ListItemButton, ListItemText,
  Stack, TextField, Typography,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';

// Koordinat "lat,lng" -> [lat, lng] atau null bila tidak valid.
const bacaKoordinat = (teks) => {
  const [lat, lng] = String(teks || '').split(',').map((v) => Number(v.trim()));
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return [lat, lng];
};
const tulisKoordinat = (lat, lng) => `${lat.toFixed(7)},${lng.toFixed(7)}`;
const PUSAT_AWAL = [-7.003011, 110.411597];

const KlikPeta = ({ onPilih }) => {
  useMapEvents({ click: (e) => onPilih(e.latlng.lat, e.latlng.lng) });
  return null;
};

// Peta di dalam dialog: ukur ulang setelah tampil, dan ikuti titik yang dipilih.
const IkutiTitik = ({ titik }) => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [map]);
  useEffect(() => {
    if (titik) map.setView(titik, Math.max(map.getZoom(), 16));
  }, [map, titik]);
  return null;
};

/**
 * Pilih lokasi cabang tanpa harus tahu angka koordinat: lokasi perangkat saat ini,
 * cari nama tempat/alamat (OpenStreetMap Nominatim), atau klik/geser penanda di peta.
 */
const PilihLokasiCabang = ({ koordinat, radius, alamat, onChange }) => {
  const titik = useMemo(() => bacaKoordinat(koordinat), [koordinat]);
  const [kataCari, setKataCari] = useState('');
  const [hasil, setHasil] = useState([]);
  const [mencari, setMencari] = useState(false);
  const [melacak, setMelacak] = useState(false);
  const [pesan, setPesan] = useState(null); // { jenis, teks }

  const pilih = (lat, lng) => onChange(tulisKoordinat(lat, lng));

  const pakaiLokasiSaya = () => {
    setPesan(null);
    if (!window.isSecureContext || !navigator.geolocation) {
      setPesan({ jenis: 'warning', teks: 'Lokasi perangkat hanya bisa dipakai lewat https (atau localhost). Gunakan pencarian atau klik peta.' });
      return;
    }
    setMelacak(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMelacak(false);
        pilih(pos.coords.latitude, pos.coords.longitude);
        setPesan({
          jenis: pos.coords.accuracy > 100 ? 'warning' : 'success',
          teks: `Lokasi diambil (akurasi ±${Math.round(pos.coords.accuracy)} m).`
            + (pos.coords.accuracy > 100 ? ' Kurang akurat: periksa titik di peta dan geser bila perlu.' : ''),
        });
      },
      (err) => {
        setMelacak(false);
        setPesan({
          jenis: 'error',
          teks: err.code === 1
            ? 'Izin lokasi ditolak browser. Izinkan lokasi untuk situs ini, atau gunakan pencarian.'
            : 'Lokasi tidak bisa diambil. Coba lagi, atau gunakan pencarian / klik peta.',
        });
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  };

  // Pencarian dijalankan saat tombol ditekan saja (bukan tiap ketikan) sesuai batas pemakaian Nominatim.
  const cari = async (teks) => {
    const q = (teks ?? kataCari).trim();
    if (!q) return;
    setMencari(true);
    setPesan(null);
    setHasil([]);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&countrycodes=id&accept-language=id&q=${encodeURIComponent(q)}`;
      const r = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = r.ok ? await r.json() : [];
      setHasil(Array.isArray(data) ? data : []);
      if (!data?.length) setPesan({ jenis: 'info', teks: 'Lokasi tidak ditemukan. Coba kata lain (mis. nama jalan + kota), atau klik peta.' });
    } catch (e) {
      setPesan({ jenis: 'error', teks: 'Pencarian gagal (periksa koneksi internet). Anda tetap bisa klik peta.' });
    } finally {
      setMencari(false);
    }
  };

  const radiusMeter = Number(radius);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Lokasi cabang</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
        <Button
          variant="outlined"
          startIcon={melacak ? <CircularProgress size={16} /> : <MyLocationIcon />}
          onClick={pakaiLokasiSaya}
          disabled={melacak}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Pakai lokasi saya sekarang
        </Button>
        {alamat?.trim() && (
          <Button variant="text" onClick={() => { setKataCari(alamat); cari(alamat); }} disabled={mencari}>
            Cari dari alamat
          </Button>
        )}
      </Stack>

      <TextField
        size="small"
        fullWidth
        placeholder="Cari nama tempat atau alamat, mis. Jl. Pandanaran Semarang"
        value={kataCari}
        onChange={(e) => setKataCari(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); cari(); } }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Button size="small" onClick={() => cari()} disabled={mencari || !kataCari.trim()}
                startIcon={mencari ? <CircularProgress size={14} /> : <SearchIcon />}>
                Cari
              </Button>
            </InputAdornment>
          ),
        }}
      />
      {hasil.length > 0 && (
        <List dense sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mt: 0.5, maxHeight: 180, overflowY: 'auto' }}>
          {hasil.map((h) => (
            <ListItemButton key={h.place_id} onClick={() => { pilih(Number(h.lat), Number(h.lon)); setHasil([]); setPesan(null); }}>
              <ListItemText primary={h.name || h.display_name.split(',')[0]} secondary={h.display_name}
                secondaryTypographyProps={{ sx: { fontSize: 12 } }} />
            </ListItemButton>
          ))}
        </List>
      )}
      {pesan && <Alert severity={pesan.jenis} sx={{ mt: 1 }}>{pesan.teks}</Alert>}

      <Box sx={{ mt: 1, height: 240, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        <MapContainer center={titik || PUSAT_AWAL} zoom={titik ? 16 : 12} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap"
          />
          <KlikPeta onPilih={pilih} />
          <IkutiTitik titik={titik} />
          {titik && (
            <>
              <Marker
                position={titik}
                draggable
                eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); pilih(p.lat, p.lng); } }}
              />
              {radiusMeter > 0 && <Circle center={titik} radius={radiusMeter} pathOptions={{ color: '#1976d2', weight: 1, fillOpacity: 0.08 }} />}
            </>
          )}
        </MapContainer>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Klik peta atau geser penanda untuk memindahkan titik.
        {radiusMeter > 0 ? ` Lingkaran = radius absen ${radiusMeter.toLocaleString('id-ID')} m.` : ''}
      </Typography>
    </Box>
  );
};

export default PilihLokasiCabang;
